import { Component, Emit, Mixins, Prop, Watch, Vue } from 'vue-property-decorator'
import { TranslateResult } from 'vue-i18n'
import { BCol, BFormGroup, BFormRow, BModal, BButton, BTabs, BTab, BBadge } from 'bootstrap-vue'
import sumBy from 'lodash/sumBy'
import { DateMixin, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import { FroalaView } from '@kwixeo/form-vue'
import ProductVariationView from '../ProductVariationView/ProductVariationView.vue'
import LotList from '../LotList/LotList.vue'
import LotUsed from '../LotUsed/LotUsed.vue'
import { fetchManualMovement, needFetchManualMovement } from '../../fetchDatas/manualMovement'
import ManualMovementDelete from '../ManualMovementDelete/ManualMovementDelete.vue'
import { mapGetters } from 'vuex'

@Component({
  components: {
    Spinner,
    BModal, BFormRow, BCol, BFormGroup, BButton, BTabs, BTab, BBadge,
    FroalaView,
    ProductVariationView,
    LotList, LotUsed, ManualMovementDelete
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule'])
  }
})
export default class ManualMovementItem extends Mixins(DateMixin, NumberMixin) {
  hasModule!: (key: string) => boolean

  @Prop({ required: true }) value!: KwixeoCatalog.StockManualMovement
  @Prop({ required: true }) modalState!: boolean

  private movement: KwixeoCatalog.StockManualMovement|null = null
  private loading = true

  get modalTitle (): string|TranslateResult {
    if (!this.movement) {
      return ''
    } else if (this.movement.type === 1) {
      return 'Détail de l\'entrée de stock'
    } else {
      return 'Détail de la sortie de stock'
    }
  }

  get countLots (): number {
    if (this.movement?.type === 1) {
      return this.movement.lots ? sumBy(this.movement.lots, lot => this.number(lot.quantity)) : 0
    }
    return this.movement?.used_lots ? sumBy(this.movement?.used_lots, lot => this.number(lot.quantity)) : 0
  }

  @Watch('value', { immediate: true })
  private async watchValue (value: KwixeoCatalog.StockManualMovement): Promise<void> {
    if (needFetchManualMovement(value)) {
      this.movement = null
      value = await fetchManualMovement(value.id)
    }
    this.movement = JSON.parse(JSON.stringify(value))
    this.loading = false
  }

  // Called when a lot is updated
  private lotUpdated (lot: KwixeoCatalog.ProductLot, old: KwixeoCatalog.ProductLot): void {
    if (this.movement?.lots) {
      const index = this.movement.lots?.findIndex(val => val.id === lot.id)
      if (index >= 0) {
        Vue.set(this.movement.lots, index, lot)
      }
    }

    this.updateLotQuantity(lot, this.number(lot.quantity) - this.number(old.quantity))
  }

  // Called when a lot is deleted
  private lotDeleted (lot: KwixeoCatalog.ProductLot): void {
    if (this.movement?.lots) {
      const index = this.movement.lots?.findIndex(value => value.id === lot.id)
      if (index >= 0) {
        Vue.delete(this.movement.lots, index)
      }
    }

    this.updateLotQuantity(lot, this.number(-lot.quantity), true)
  }

  // Apply a lot update or delete
  private updateLotQuantity (lot: KwixeoCatalog.ProductLot, quantity: number, toDelete = false): void {
    const movement = JSON.parse(JSON.stringify(this.movement)) as KwixeoCatalog.StockManualMovement
    const contentIndex = movement.contents.findIndex(content => content.product_id === lot.product_id)
    if (contentIndex === -1) {
      return
    }

    const content = movement.contents[contentIndex]
    if (!content.lots) {
      return
    }

    content.quantity = this.number(content.quantity) + quantity

    const lotIndex = content.lots.findIndex(val => val.id === lot.id)
    if (lotIndex >= 0) {
      if (toDelete) {
        content.lots.splice(lotIndex, 1)
      } else {
        content.lots[lotIndex] = lot
      }
    }

    movement.contents[contentIndex] = content

    this.$emit('input', movement)
  }

  @Emit('deleted')
  private deleted (movement: KwixeoCatalog.ProductLot): KwixeoCatalog.ProductLot {
    const modal = this.$refs.modal as BModal
    modal.hide()
    return movement
  }
}
